/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */


 function delayAnimation() {
    let x = window.screen.width;

    function check_price_size() {
        const product_price_block = Array.from(document.querySelectorAll(".product-block .block-inner .product-price"));
        const product_title_block = Array.from(document.querySelectorAll(".product-block .block-inner .product-block__title"));
    
        let bigger_price = 0;
        let bigger_price_string;
    
        product_price_block.forEach((price_inner) => {
            price_inner.style.height = "auto";
            if (price_inner.offsetHeight > bigger_price) {
                bigger_price = price_inner.offsetHeight;
            }
        });
    
        bigger_price_string = bigger_price.toString() + 'px';
    
        product_title_block.forEach((block_title) => {
            // block_title.style.paddingBottom = bigger_price_string;
        });
    }
    
    function check_inner_size() {
        const array_product_block = Array.from(document.querySelectorAll(".product-block .block-inner .block-inner-inner"));
        const cart_drawer_blocks = array_product_block.filter((block_inner) => {
            return block_inner.closest('.cart-drawer, .js-cart-drawer, cart-drawer, .cart-item-upsells');
        });
        const filtered_blocks = array_product_block.filter((block_inner) => {
            return !block_inner.closest('.cart-drawer, .js-cart-drawer, cart-drawer, .cart-item-upsells');
        });
        
        cart_drawer_blocks.forEach((block_inner) => {
            block_inner.style.height = "";
        });
        
        let bigger_block = 0;
        let bigger_block_string;
    
        filtered_blocks.forEach((block_inner) => {
            block_inner.style.height = "auto";
            if (block_inner.offsetHeight > bigger_block) {
                bigger_block = block_inner.offsetHeight;
            }
        });
    
        bigger_block_string = bigger_block.toString() + 'px';
    
        filtered_blocks.forEach((block_inner) => {
            block_inner.style.height = bigger_block_string;
        });
    }
    
    document.addEventListener('theme:ajax:done', () => {
        check_price_size();
        check_inner_size();
    });

    window.addEventListener('resize', function() {
        x = window.screen.width;
        check_price_size();
        check_inner_size();
    });
    
    check_price_size();
    check_inner_size();
}

// Initialize the animation function
delayAnimation();

// Wait for window load event and then run with delay
window.addEventListener('load', function() {
    setTimeout(delayAnimation, 1000);
});

document.addEventListener('click', function (e) {
    const header = e.target.closest('.filter-menu-header');
  
    if (header) {
      const isActive = header.classList.contains('active');
  
      // Remove active from all headers and items
      document.querySelectorAll('.filter-menu-header').forEach(h => h.classList.remove('active'));
      document.querySelectorAll('.filter-menu-items').forEach(i => i.classList.remove('active'));
  
      if (!isActive) {
        const group = header.closest('.filter__menu-group');
        const items = group.querySelector('.filter-menu-items');
        header.classList.add('active');
        items.classList.add('active');
      }
    }
  });

/**
 * Update robots tag dynamically when pagination changes
 * This is necessary because AJAX navigation doesn't reload the <head>
 */
(function() {
  'use strict';

  function isPaginationPage() {
    const path = window.location.pathname;
    
    if (path.includes('/blogs/')) {
      return false;
    }
    
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');
    
    return pageParam && parseInt(pageParam) > 1;
  }

  function getTemplateName() {
    const body = document.body;
    const classes = body.className.split(' ');
    const templateClass = classes.find(cls => cls.startsWith('template-'));
    
    if (templateClass) {
      return templateClass.replace('template-', '');
    }
    
    const path = window.location.pathname;
    if (path.includes('/collections/')) return 'collection';
    if (path.includes('/search')) return 'search';
    if (path.includes('/blogs/')) return 'blog';
    if (path.includes('/products/')) return 'product';
    
    return 'index';
  }

  function updateRobotsTag() {
    const templateName = getTemplateName();
    const isPagination = isPaginationPage();
    
    let robotsTag = document.querySelector('meta[name="robots"]');
    
    if (!robotsTag) {
      robotsTag = document.createElement('meta');
      robotsTag.setAttribute('name', 'robots');
      document.head.appendChild(robotsTag);
    }
    
    // Collection pagination pages stay INDEX,FOLLOW (same as page 1).
    // Search pagination keeps NOINDEX,FOLLOW. Other templates stay INDEX,FOLLOW.
    let robotsContent = 'INDEX,FOLLOW';
    
    if (isPagination && templateName === 'search') {
      robotsContent = 'NOINDEX,FOLLOW';
    }
    
    robotsTag.setAttribute('content', robotsContent);
  }

  document.addEventListener('theme:ajax:done', function() {
    setTimeout(updateRobotsTag, 100);
  });

  window.addEventListener('popstate', function() {
    setTimeout(updateRobotsTag, 100);
  });

  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function() {
    originalPushState.apply(history, arguments);
    setTimeout(updateRobotsTag, 100);
  };

  history.replaceState = function() {
    originalReplaceState.apply(history, arguments);
    setTimeout(updateRobotsTag, 100);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateRobotsTag);
  } else {
    updateRobotsTag();
  }
})();


/**
 * =================================================================================================
 * Quick Add to Cart Button - ColoredByte
 * =================================================================================================
 * 
 * This script handles the quick add to cart functionality.
 * When a user clicks the quick add to cart button, it:
 * 1. Adds the product directly to the cart (without opening quick view)
 * 2. Opens the cart drawer (or refreshes if already inside cart drawer)
 */
(function() {
  'use strict';

  // Check if button is inside cart drawer
  function isInsideCartDrawer(element) {
    return element.closest('.cart-drawer, .js-cart-drawer, cart-drawer') !== null;
  }

  // Button works on desktop and mobile (icon-only quick add)
  function shouldButtonWork(button) {
    return true;
  }

  async function addToCartQuick(button) {
    if (!shouldButtonWork(button)) return;
    
    const variantId = button.dataset.variantId;
    const productTitle = button.dataset.productTitle;

    if (!variantId) {
      console.error('Quick ATC: No variant ID found');
      return;
    }

    // Set loading state
    button.classList.add('is-loading');
    button.disabled = true;

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    
    // Get section IDs for cart update
    const sectionsToUpdate = ['page-header', 'cart-drawer']
      .map((sel) => document.querySelector(sel))
      .filter((el) => el);
    const sectionIds = sectionsToUpdate.map((el) => el.dataset.sectionId);
    
    formData.append('sections_url', window.location.pathname);
    formData.append('sections', sectionIds.join(','));

    const fetchRequestOpts = {
      method: 'POST',
      headers: {
        'Accept': 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    };

    try {
      const response = await fetch(window.theme?.routes?.cartAdd || '/cart/add.js', fetchRequestOpts);
      const data = await response.json();

      if (data.status) {
        // Error occurred
        const error = typeof data.description === 'string' ? data.description : data.message;
        console.error('Quick ATC Error:', error);
        button.classList.remove('is-loading');
        button.disabled = false;
        
        // Show error feedback
        alert(error || 'Could not add item to cart');
        return;
      }

      if (!response.ok) throw new Error(response.status);

      // Update cart sections
      if (data.sections && sectionsToUpdate.length === Object.keys(data.sections).length) {
        sectionsToUpdate.forEach((el) => {
          if (el.updateFromCartChange) {
            el.updateFromCartChange(data.sections[el.dataset.sectionId]);
          } else if (el.updateFromCartChan) {
            el.updateFromCartChan(data.sections[el.dataset.sectionId]);
          }
        });
      }
      
      // If inside cart drawer, refresh the cart form
      if (isInsideCartDrawer(button)) {
        const cartForm = button.closest('.cart-drawer, .js-cart-drawer, cart-drawer')?.querySelector('cart-form');
        if (cartForm && cartForm.refresh) {
          cartForm.refresh();
        } else {
          document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        }
      } else {
        // Fallback: dispatch event to refresh cart
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      }

      // Open cart drawer only if not already inside it
      if (!isInsideCartDrawer(button)) {
        const cartDrawer = document.querySelector('.js-cart-drawer');
        if (cartDrawer && cartDrawer.open) {
          cartDrawer.open(button);
        } else {
          // Fallback: dispatch event to open cart drawer
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
            bubbles: true,
            detail: { opener: button }
          }));
        }
      }

      // Dispatch cart add event
      document.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: { variantId: data.variant_id }
      }));

      // Show success state (icon-only: visual feedback only)
      button.classList.remove('is-loading');
      button.classList.add('is-success');

      // Reset after 2 seconds
      setTimeout(() => {
        button.classList.remove('is-success');
        button.disabled = false;
      }, 2000);

    } catch (error) {
      console.error('Quick ATC Error:', error);
      button.classList.remove('is-loading');
      button.disabled = false;
      
      // Dispatch error event
      document.dispatchEvent(new CustomEvent('on:cart:error', {
        bubbles: true,
        detail: { error: 'Could not add item to cart' }
      }));
    }
  }

  // Event delegation for quick add to cart buttons (icon button, desktop + mobile)
  document.addEventListener('click', function(evt) {
    const button = evt.target.closest('.quick-add-to-cart-btn');
    
    if (button && !button.disabled && shouldButtonWork(button)) {
      evt.preventDefault();
      evt.stopPropagation();
      addToCartQuick(button);
    }
  });

  // Re-initialize on AJAX navigation
  document.addEventListener('theme:ajax:done', function() {
    // Buttons are already handled by event delegation
  });

})();

// =================================================================================================
// Shipping Calculator Components - Migrated from backgammon-galaxy
// =================================================================================================

// Country Province Selector
if (!customElements.get('country-province')) {
  customElements.define(
    'country-province',
    class CountryProvince extends HTMLElement {
      constructor() {
        super();

        this.provinceElement = this.querySelector('[name="address[province]"]');
        this.countryElement = this.querySelector('[name="address[country]"]');
        this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));

        if (this.getAttribute('data-country') !== '' && this.getAttribute('data-country') !== null) {
          this.countryElement.selectedIndex = Math.max(0, Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.getAttribute('data-country')));
          this.countryElement.dispatchEvent(new Event('change'));
        }
        else {
          this.handleCountryChange();
        }
      }

      handleCountryChange() {
        const option = this.countryElement.options[this.countryElement.selectedIndex];
        const provinces = JSON.parse(option.getAttribute('data-provinces') || '[]');
        
        // Find the container with hidden attribute - could be parentElement or an ancestor .field
        const provinceContainer = this.provinceElement.closest('.field[hidden]') 
          || this.provinceElement.closest('.field') 
          || this.provinceElement.parentElement;
        
        provinceContainer.hidden = provinces.length === 0;

        if (provinces.length === 0) {
          return;
        }

        this.provinceElement.innerHTML = '';

        provinces.forEach((data) => {
          const selected = data[1] === this.getAttribute('data-province');
          this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
        });
      }
    }
  );
}

// Shipping Calculator Form (using autonomous custom element for Safari compatibility)
if (!customElements.get('shipping-calculator')) {
  customElements.define(
    'shipping-calculator',
    class ShippingCalculator extends HTMLElement {
      constructor() {
        super();
        this.onSubmitHandler = this.onSubmit.bind(this);
      }

      connectedCallback() {
        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        this.resultsElement = this.querySelector('.shipping-calculator-results');
        this.zipInput = this.querySelector('[name="address[zip]"]');

        if (this.form) {
          this.form.addEventListener('submit', this.onSubmitHandler);
        }
        if (this.submitButton) {
          this.submitButton.addEventListener('click', this.onSubmitHandler);
        }
        // Allow Enter key on zip input
        if (this.zipInput) {
          this.zipInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              this.onSubmit(e);
            }
          });
        }
      }

      disconnectedCallback() {
        this.abortController?.abort();
        if (this.form) {
          this.form.removeEventListener('submit', this.onSubmitHandler);
        }
        if (this.submitButton) {
          this.submitButton.removeEventListener('click', this.onSubmitHandler);
        }
      }

      onSubmit(event) {
        event.preventDefault();

        this.abortController?.abort();
        this.abortController = new AbortController();

        const zip = this.querySelector('[name="address[zip]"]')?.value || '',
          country = this.querySelector('[name="address[country]"]')?.value || '',
          province = this.querySelector('[name="address[province]"]')?.value || '';

        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.classList.add('is-loading');
        
        // Show loader, hide text
        const btnText = this.submitButton.querySelector('.shipping-calculator-btn__text');
        const btnLoader = this.submitButton.querySelector('.shipping-calculator-btn__loader');
        if (btnText) btnText.hidden = true;
        if (btnLoader) btnLoader.hidden = false;

        const params = `shipping_address[zip]=${encodeURIComponent(zip)}&shipping_address[country]=${encodeURIComponent(country)}&shipping_address[province]=${encodeURIComponent(province)}`;
        const sectionUrl = `${theme.routes.cart}/shipping_rates.json?${params}`;

        fetch(sectionUrl, { signal: this.abortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.shipping_rates) {
              this.formatShippingRates(parsedState.shipping_rates);
            }
            else {
              this.formatError(parsedState);
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          })
          .finally(() => {
            if (this.resultsElement) {
              this.resultsElement.hidden = false;
            }
            this.submitButton.removeAttribute('aria-busy');
            this.submitButton.classList.remove('is-loading');
            
            // Hide loader, show text
            if (btnText) btnText.hidden = false;
            if (btnLoader) btnLoader.hidden = true;
          });
      }

      formatError(errors) {
        if (!this.resultsElement) return;
        
        const shippingRatesList = Object.keys(errors).map((errorKey) => {
          return `<li>${errors[errorKey]}</li>`;
        });
        this.resultsElement.classList.remove('success');
        this.resultsElement.classList.add('error');
        const strongEl = this.resultsElement.querySelector('strong');
        if (strongEl) {
          strongEl.textContent = theme.strings?.shippingCalculator?.noRates || 'Error';
        }
        const ratesContainer = this.resultsElement.querySelector('.shipping-rates-result');
        if (ratesContainer) {
          ratesContainer.innerHTML = `<ul class="styled-list">${shippingRatesList.join('')}</ul>`;
        }
      }

      formatShippingRates(shippingRates) {
        if (!this.resultsElement) return;
        
        const shippingRatesList = shippingRates.map(({ name, price }) => {
          const format = theme.settings?.moneyWithCurrencyFormat || '{{amount}}';
          const formattedPrice = format.replace(/\{\{\s*(\w+)\s*\}\}/, price);
          return `<li>${name}: ${formattedPrice}</li>`;
        });
        
        const strongEl = this.resultsElement.querySelector('strong');
        const ratesContainer = this.resultsElement.querySelector('.shipping-rates-result');
        
        if (shippingRates.length) {
          this.resultsElement.classList.remove('error');
          this.resultsElement.classList.add('success');
          const headingText = shippingRates.length === 1 
            ? (theme.strings?.shippingCalculator?.singleRate || 'There is one shipping rate for your address:')
            : (theme.strings?.shippingCalculator?.multipleRates || 'There are multiple shipping rates for your address:');
          if (strongEl) {
            strongEl.textContent = headingText;
          }
        }
        else {
          this.resultsElement.classList.remove('success');
          this.resultsElement.classList.add('error');
          if (strongEl) {
            strongEl.textContent = theme.strings?.shippingCalculator?.noRates || 'We do not ship to this destination.';
          }
        }
        
        if (ratesContainer) {
          ratesContainer.innerHTML = shippingRatesList.length > 0 
            ? `<ul class="styled-list">${shippingRatesList.join('')}</ul>` 
            : '';
        }
      }
    }
  );
}

// =================================================================================================
// Klaviyo form headings: demote h1 → h4 (SEO — page should have only one h1)
// Forms inject HTML dynamically; heading level is controlled in Klaviyo but may still render as h1.
// =================================================================================================
(function () {
  'use strict';

  const KLAVIYO_FORM_SELECTOR = '.klaviyo-form, [class*="klaviyo-form-"]';

  function demoteKlaviyoFormHeadings(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const forms = scope.matches?.(KLAVIYO_FORM_SELECTOR)
      ? [scope]
      : scope.querySelectorAll(KLAVIYO_FORM_SELECTOR);

    forms.forEach((form) => {
      form.querySelectorAll('h1').forEach((heading) => {
        const h4 = document.createElement('h4');
        Array.from(heading.attributes).forEach((attr) => {
          h4.setAttribute(attr.name, attr.value);
        });
        h4.innerHTML = heading.innerHTML;
        heading.replaceWith(h4);
      });
    });
  }

  function init() {
    demoteKlaviyoFormHeadings(document);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (
            node.matches?.(KLAVIYO_FORM_SELECTOR) ||
            node.querySelector?.(KLAVIYO_FORM_SELECTOR) ||
            node.closest?.(KLAVIYO_FORM_SELECTOR)
          ) {
            demoteKlaviyoFormHeadings(node.closest?.(KLAVIYO_FORM_SELECTOR) || node);
          }
        });
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
